import { Combobox, Transition } from '@headlessui/react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { Fragment, useState } from 'react'

import type { OptionProps, SelectProps } from '~/types/component'

const Select = ({
  placeholder,
  icon,
  rounded,
  font,
  options,
  disabled,
  isLoading,
  isLocation,
  selected,
  setSelected,
  errorMessage,
}: SelectProps) => {
  const [query, setQuery] = useState('')

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')),
        )

  return (
    <div className="relative">
      <Combobox key={selected?.value} value={selected} onChange={setSelected} disabled={disabled || isLoading}>
        {({ open }) => (
          <div className="relative">
            <div
              className={clsx(
                'relative w-full cursor-default overflow-hidden rounded-lg bg-jba-gray text-left focus:outline-none',
                [rounded === 'xl' ? 'rounded-xl' : 'rounded-lg'],
                [errorMessage && 'ring-1 ring-jba-red'],
              )}
            >
              <Combobox.Button as="div">
                <Combobox.Input
                  className={clsx(
                    'h-[42px] w-full border-none bg-jba-gray py-2 pr-10 leading-5 text-jba-blue focus:ring-0',
                    [icon || isLoading ? 'pl-10' : 'pl-4'],
                    [font === 'lg' ? 'text-lg' : 'text-xs'],
                    [errorMessage ? 'placeholder:text-jba-red' : 'placeholder:text-gray-400'],
                  )}
                  displayValue={(option: OptionProps) => option.label}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={isLoading ? 'Loading data...' : placeholder}
                />
                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <Icon
                    icon="material-symbols:keyboard-arrow-down-rounded"
                    className={clsx('text-lg text-gray-400 transition-all', [open && 'rotate-180 text-jba-blue'])}
                  />
                </span>
              </Combobox.Button>
              {icon && !isLoading && (
                <Icon
                  icon={icon}
                  className={clsx(
                    'absolute inset-y-0 left-3 my-auto inline-block text-lg',
                    [open || selected ? 'text-jba-blue' : 'text-gray-400'],
                    [errorMessage && !selected && 'text-jba-red'],
                    [isLocation && !disabled && 'text-jba-red'],
                  )}
                />
              )}
              {isLoading && (
                <Icon
                  icon="material-symbols:progress-activity"
                  className="absolute inset-y-0 left-3 my-auto inline-block animate-spin text-lg text-gray-400"
                />
              )}
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full min-w-min overflow-auto rounded-lg bg-jba-gray py-1 text-xs shadow-lg focus:outline-none">
                {filteredOptions.length === 0 && query !== '' ? (
                  <div className="relative cursor-default select-none px-4 py-2 text-gray-400">Nothing found.</div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <Combobox.Option
                      key={index}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-4 pr-10 text-jba-blue ${active && 'bg-gray-50'}`
                      }
                      value={option}
                    >
                      <span className="block truncate">{option.label}</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {selected?.value === option.value ? (
                          <Icon icon="gg:radio-checked" className="text-jba-blue" />
                        ) : (
                          <Icon icon="gg:radio-check" className="text-gray-400" />
                        )}
                      </span>
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
      {errorMessage && <span className="mt-4 text-xs text-jba-red">{errorMessage}</span>}
    </div>
  )
}

export default Select
